import {
  arrow,
  computePosition,
  offset,
  flip,
  shift,
  autoUpdate,
} from '@floating-ui/dom';
import { gsap } from 'gsap';

const DEFAULTS = {
  placement: 'top',
  width: null, // CSS width value, e.g. '240px' or '20rem'
  content: null, // string or HTMLElement (cloned if element)
  contentSelector: null, // CSS selector for content to clone
  tooltipClass: 'tooltip',
  hasArrow: true,
  arrowClass: 'tooltip__arrow',
  interaction: 'click', // 'hover' | 'click' | 'click-hover'
  hoverDelay: 300,
  offset: 8,
  shiftPadding: 12,
  strategy: 'fixed',
  hoverDesktopMinWidth: 1024, // enable hover only on >= this width
  animation: {
    enterDuration: 0.2,
    leaveDuration: 0.15,
    easeIn: 'power2.out',
    easeOut: 'power2.in',
  },
};

/**
 * Tooltip controller bound to a single trigger element.
 * Supports configuration via data-attributes and/or constructor options.
 *
 * Data attributes supported (camelCase below => kebab-case in HTML):
 * - data-tooltip: string content fallback
 * - data-placement
 * - data-tooltip-width
 * - data-tooltip-content-selector
 * - data-tooltip-class
 * - data-tooltip-interaction: 'hover' | 'click' | 'click-hover'
 * - data-tooltip-hover-delay
 */
export class Tooltip {
  constructor(btn, options = {}) {
    this.btn = btn;

    // Collect options from data-attrs
    const datasetOptions = {
      placement: btn.dataset.placement,
      width: btn.dataset.tooltipWidth,
      contentSelector: btn.dataset.tooltipContentSelector,
      tooltipClass: btn.dataset.tooltipClass,
      hasArrow: btn.dataset.hasArrow,
      arrowClass: btn.dataset.arrowClass,
      interaction: btn.dataset.tooltipInteraction,
      hoverDelay: btn.dataset.tooltipHoverDelay
        ? parseInt(btn.dataset.tooltipHoverDelay, 10)
        : undefined,
      // content string fallback
      content:
        typeof btn.dataset.tooltip === 'string'
          ? btn.dataset.tooltip
          : undefined,
    };

    // Merge: defaults <- data-attrs <- passed options
    this.options = {
      ...DEFAULTS,
      ...cleanUndefined(datasetOptions),
      ...cleanUndefined(options),
    };

    // Runtime state
    this.tooltipEl = null;
    this.cleanupAutoUpdate = null;
    this.hoverTimer = null;
    this.isHovering = false;
    this.hasAnimatedIn = false;

    // Bind handlers once for add/removeEventListener symmetry
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleHoverStart = this.handleHoverStart.bind(this);
    this.handleHoverEnd = this.handleHoverEnd.bind(this);

    // Attach listeners based on interaction type
    this.attachEvents();
  }

  attachEvents() {
    const type = this.options.interaction;
    if (type === 'hover') {
      this.btn.addEventListener('mouseenter', this.handleHoverStart);
      this.btn.addEventListener('mouseleave', this.handleHoverEnd);
    } else if (type === 'click-hover') {
      this.btn.addEventListener('click', this.handleClick);
      this.btn.addEventListener('mouseenter', this.handleHoverStart);
      this.btn.addEventListener('mouseleave', this.handleHoverEnd);
    } else {
      // 'click' (default)
      this.btn.addEventListener('click', this.handleClick);
    }
  }

  detachEvents() {
    const type = this.options.interaction;
    this.btn.removeEventListener('click', this.handleClick);
    this.btn.removeEventListener('mouseenter', this.handleHoverStart);
    this.btn.removeEventListener('mouseleave', this.handleHoverEnd);
    document.removeEventListener('click', this.handleOutsideClick);
  }

  handleClick() {
    if (this.tooltipEl) {
      this.hide();
    } else {
      this.show();
    }
  }

  // Delayed hover logic (desktop only)
  handleHoverStart() {
    if (window.innerWidth < this.options.hoverDesktopMinWidth) return;

    this.isHovering = true;
    if (this.hoverTimer) clearTimeout(this.hoverTimer);

    this.hoverTimer = setTimeout(() => {
      this.hoverTimer = null;
      if (!this.isHovering) return;
      if (this.tooltipEl) return; // already open (e.g., via click)
      this.show();
    }, this.options.hoverDelay);
  }

  handleHoverEnd() {
    this.isHovering = false;
    if (this.hoverTimer) {
      clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
    }
    this.hide();
  }

  handleOutsideClick(e) {
    if (
      this.tooltipEl &&
      !this.tooltipEl.contains(e.target) &&
      !this.btn.contains(e.target)
    ) {
      this.hide();
    }
  }

  show() {
    if (this.tooltipEl) {
      this.hide();
      return;
    }

    const {
      placement,
      width,
      content,
      contentSelector,
      tooltipClass,
      hasArrow,
      arrowClass,
      interaction,
      strategy,
      offset: off,
      shiftPadding,
      animation,
    } = this.options;

    // Build tooltip element
    const el = document.createElement('div');
    el.className = tooltipClass || DEFAULTS.tooltipClass;
    if (width) el.style.setProperty('--width', `${width}`);

    // Resolve content: options.content > contentSelector > data-tooltip > ''
    let resolvedContent = null;
    if (content instanceof HTMLElement) {
      resolvedContent = content.cloneNode(true);
      resolvedContent.hidden = false;
      el.appendChild(resolvedContent);
    } else if (typeof content === 'string' && content.length) {
      el.innerHTML = content;
    } else if (contentSelector) {
      const src = document.querySelector(contentSelector);
      if (src) {
        const cloned = src.cloneNode(true);
        cloned.hidden = false;
        el.appendChild(cloned);
      } else {
        el.textContent = this.btn.dataset.tooltip || '';
      }
    } else {
      el.innerHTML = this.btn.dataset.tooltip || '';
    }

    let arrowEl;
    if (hasArrow) {
      arrowEl = document.createElement('span');
      arrowEl.className = arrowClass || DEFAULTS.arrowClass;
      el.appendChild(arrowEl);
    }

    this.tooltipEl = el;
    this.hasAnimatedIn = false;
    document.body.appendChild(el);

    this.btn.setAttribute('aria-expanded', 'true');

    let middleware = [offset(off), flip(), shift({ padding: shiftPadding })];
    if (hasArrow && arrowEl) {
      middleware.push(arrow({ element: arrowEl, padding: 16 }));
    }

    // Position and auto-update
    this.cleanupAutoUpdate = autoUpdate(this.btn, el, () => {
      computePosition(this.btn, el, {
        placement,
        strategy,
        middleware,
      }).then(({ x, y, placement, middlewareData }) => {
        if (middlewareData.arrow) {
          const { x, y } = middlewareData.arrow;

          gsap.set(arrowEl, {
            left: x != null ? `${x + 6}px` : '',
            top: y != null ? `${y}px` : '',
          });
        }

        gsap.set(el, {
          position: strategy,
          left: `${x}px`,
          top: `${y}px`,
        });

        el.dataset.position = placement;

        if (!this.hasAnimatedIn) {
          this.hasAnimatedIn = true;
          const base = placement.split('-')[0];
          const fromVars = { opacity: 0 };
          switch (base) {
            case 'top':
              fromVars.y = 6;
              break;
            case 'bottom':
              fromVars.y = -6;
              break;
            case 'left':
              fromVars.x = 6;
              break;
            case 'right':
              fromVars.x = -6;
              break;
          }
          gsap.fromTo(el, fromVars, {
            opacity: 1,
            x: 0,
            y: 0,
            duration: animation.enterDuration,
            ease: animation.easeIn,
          });
        }
      });
    });

    // Only add outside click listener for click-based tooltips
    if (interaction !== 'hover') {
      document.addEventListener('click', this.handleOutsideClick);
    }
  }

  hide() {
    if (!this.tooltipEl) return;

    const el = this.tooltipEl;
    const { animation } = this.options;
    const placement = el.dataset.position || this.options.placement || 'top';
    const base = placement.split('-')[0];

    const toVars = {
      opacity: 0,
      duration: animation.leaveDuration,
      ease: animation.easeOut,
      onComplete: () => {
        this.cleanupTooltip();
        this.btn.setAttribute('aria-expanded', 'false');
      },
    };

    switch (base) {
      case 'top':
        toVars.y = 6;
        break;
      case 'bottom':
        toVars.y = -6;
        break;
      case 'left':
        toVars.x = 6;
        break;
      case 'right':
        toVars.x = -6;
        break;
    }

    gsap.to(el, toVars);
  }

  cleanupTooltip() {
    if (this.tooltipEl) {
      this.tooltipEl.remove();
      this.tooltipEl = null;
    }
    if (this.cleanupAutoUpdate) {
      this.cleanupAutoUpdate();
      this.cleanupAutoUpdate = null;
    }
    if (this.hoverTimer) {
      clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
    }
    this.isHovering = false;

    document.removeEventListener('click', this.handleOutsideClick);
  }

  destroy() {
    this.hide();
    this.detachEvents();
  }
}

function cleanUndefined(obj) {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined),
  );
}

/**
 * Factory: initialize one tooltip for a given trigger button.
 * Returns the Tooltip instance.
 */
export function initTooltip(btn, options = {}) {
  return new Tooltip(btn, options);
}

/**
 * Initialize all tooltips inside a parent (defaults to document).
 * Preserves original signature/behavior.
 * Returns an array of Tooltip instances.
 */
export function initTooltips(parent = document, options = {}) {
  const instances = [];
  parent.querySelectorAll('.js-tooltip').forEach((btn) => {
    instances.push(initTooltip(btn, options));
  });
  return instances;
}
